import type {
  AgentStatus,
  DeliveryMode,
  WorkspaceFileContent,
  WorkspaceFileInfo,
} from "@remote-agents/shared";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  AuthError,
  fetchAgentFile,
  fetchAgentFiles,
  requestAgentMarkdown,
} from "../api";
import { formatBytes, formatElapsed, formatRelative } from "../format";
import { useNow } from "../hooks";
import { useToast } from "../toast";
import { MarkdownReader } from "./MarkdownReader";

type Props = {
  agentId: string;
  agentName: string;
  agentStatus: AgentStatus;
  cwd?: string;
  onAuthLost: () => void;
};

type PendingWrite = {
  path: string;
  startedAt: number;
  /** mtime of the file before we asked, so we detect a fresh write (null = didn't exist). */
  prevMtime: number | null;
};

const LIST_POLL_MS = 20_000;
const WRITE_POLL_MS = 5_000;
const WRITE_TIMEOUT_MS = 5 * 60_000;

export function MarkdownWorkspace({
  agentId,
  agentName,
  agentStatus,
  cwd,
  onAuthLost,
}: Props) {
  const toast = useToast();
  const [files, setFiles] = useState<WorkspaceFileInfo[] | null>(null);
  const [filter, setFilter] = useState("");
  const [doc, setDoc] = useState<WorkspaceFileContent | null>(null);
  const [openingPath, setOpeningPath] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [reqPath, setReqPath] = useState("");
  const [instruct, setInstruct] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [pending, setPending] = useState<PendingWrite | null>(null);
  const now = useNow(1000, pending !== null);
  const prevStatusRef = useRef(agentStatus);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const loadList = useCallback(
    async (silent = true) => {
      try {
        const next = await fetchAgentFiles(agentId);
        next.sort((a, b) => b.mtime - a.mtime);
        setFiles(next);
        setListError(null);
      } catch (err) {
        if (err instanceof AuthError) {
          onAuthLost();
          return;
        }
        if (!silent) {
          setListError(
            err instanceof Error ? err.message : "Could not list files"
          );
        }
      }
    },
    [agentId, onAuthLost]
  );

  // Initial load + slow background refresh.
  useEffect(() => {
    setFiles(null);
    setDoc(null);
    setPending(null);
    void loadList(false);
    const t = window.setInterval(() => void loadList(), LIST_POLL_MS);
    return () => window.clearInterval(t);
  }, [loadList]);

  const openPath = useCallback(
    async (rel: string, silent = false) => {
      const clean = rel.trim();
      if (!clean) return;
      setOpeningPath(clean);
      try {
        const file = await fetchAgentFile(agentId, clean);
        setDoc(file);
        setListError(null);
      } catch (err) {
        if (err instanceof AuthError) {
          onAuthLost();
          return;
        }
        if (!silent) {
          toast.push(
            err instanceof Error ? err.message : `Could not open ${clean}`,
            "error"
          );
        }
      } finally {
        setOpeningPath(null);
      }
    },
    [agentId, onAuthLost, toast]
  );

  // Refresh the list when the agent goes back to idle (it may have just written files).
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = agentStatus;
    if (prev === "running" && agentStatus !== "running") {
      void loadList();
    }
  }, [agentStatus, loadList]);

  // While waiting for a requested file: poll, auto-open on success, time out.
  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    const check = async () => {
      const current = pendingRef.current;
      if (!current || cancelled) return;
      try {
        const file = await fetchAgentFile(agentId, current.path);
        if (cancelled) return;
        const fresh =
          current.prevMtime === null
            ? file.content.trim().length > 0
            : file.mtime > current.prevMtime;
        if (fresh) {
          setPending(null);
          setDoc(file);
          toast.push(`${current.path} is ready`, "success");
          void loadList();
          return;
        }
      } catch {
        /* file not written yet */
      }
      if (Date.now() - current.startedAt > WRITE_TIMEOUT_MS) {
        setPending(null);
        toast.push(
          `${current.path} still isn't ready after 5 minutes. Check the live activity feed.`,
          "error"
        );
      }
    };
    const t = window.setInterval(() => void check(), WRITE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [pending, agentId, loadList, toast]);

  async function onRequest(mode: DeliveryMode, e?: FormEvent) {
    e?.preventDefault();
    const clean = reqPath.trim().replace(/^\/+/, "");
    if (!clean) {
      toast.push("Enter a file name first, e.g. report.md", "error");
      return;
    }
    setRequesting(true);
    try {
      // Capture the current mtime (if the file exists) so we can tell old from new.
      let prevMtime: number | null = null;
      try {
        const existing = await fetchAgentFile(agentId, clean);
        prevMtime = existing.mtime;
      } catch {
        /* file does not exist yet */
      }
      await requestAgentMarkdown(agentId, {
        path: clean,
        instruct: instruct.trim() || undefined,
        mode,
      });
      setPending({ path: clean, startedAt: Date.now(), prevMtime });
      toast.push(
        mode === "interrupt"
          ? `${agentName} is writing ${clean} now`
          : `${clean} queued for ${agentName}`,
        "success"
      );
      setInstruct("");
    } catch (err) {
      if (err instanceof AuthError) {
        onAuthLost();
        return;
      }
      toast.push(
        err instanceof Error ? err.message : "Request failed",
        "error"
      );
    } finally {
      setRequesting(false);
    }
  }

  async function onCopy() {
    if (!doc) return;
    try {
      await navigator.clipboard.writeText(doc.content);
      toast.push("Copied to clipboard", "success");
    } catch {
      toast.push("Copy failed — your browser blocked clipboard access", "error");
    }
  }

  const visibleFiles = (files ?? []).filter((f) =>
    f.path.toLowerCase().includes(filter.trim().toLowerCase())
  );

  const nextCheckIn = pending
    ? Math.max(0, WRITE_POLL_MS - ((now - pending.startedAt) % WRITE_POLL_MS))
    : 0;

  const viewer = doc ? (
    <MarkdownReader key={doc.path} doc={doc} onCopy={() => void onCopy()} />
  ) : (
    <div className="md-viewer md-viewer--empty">
      <p className="muted">
        {files === null
          ? "Loading files…"
          : files.length === 0
            ? "No markdown files in this workspace yet. Ask the agent to write one below."
            : "Pick a file on the left to read it here."}
      </p>
    </div>
  );

  return (
    <section className="card md-ws">
      <div className="feed__head">
        <h2>Files</h2>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void loadList(false)}
        >
          Refresh
        </button>
      </div>
      {cwd ? (
        <p className="muted md-ws__root">
          Files live in <code>{cwd}</code>
        </p>
      ) : null}
      {listError ? <p className="form-error">{listError}</p> : null}

      <div className="md-ws__panes">
        <div className="md-ws__list">
          <input
            className="md-ws__filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter files…"
            autoComplete="off"
            aria-label="Filter files"
          />
          {files === null ? (
            <div className="skeleton-stack">
              <div className="skeleton" />
              <div className="skeleton" />
              <div className="skeleton" />
            </div>
          ) : visibleFiles.length === 0 ? (
            <p className="muted md-ws__none">
              {files.length === 0 ? "No files yet." : "No files match the filter."}
            </p>
          ) : (
            <ul className="md-files">
              {visibleFiles.map((file) => (
                <li key={file.path}>
                  <button
                    type="button"
                    className={
                      "md-file" + (doc?.path === file.path ? " md-file--active" : "")
                    }
                    disabled={openingPath !== null}
                    onClick={() => void openPath(file.path)}
                  >
                    <span className="md-file__name">
                      {openingPath === file.path ? "Opening… " : ""}
                      {file.path}
                    </span>
                    <span className="md-file__meta">
                      {formatBytes(file.bytes)} · {formatRelative(file.mtime)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {viewer}
      </div>

      <form
        className="md-ws__ask"
        onSubmit={(e) => void onRequest("queue", e)}
      >
        <h3 className="md-ws__ask-title">Ask {agentName} to write a file</h3>
        {pending ? (
          <div className="md-ws__waiting">
            <p>
              {agentName} is writing <strong>{pending.path}</strong> —{" "}
              {formatElapsed(pending.startedAt, now)}
            </p>
            <p className="muted">
              Checking again in {Math.ceil(nextCheckIn / 1000)}s. It opens here
              automatically when ready.
            </p>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setPending(null)}
            >
              Stop waiting
            </button>
          </div>
        ) : (
          <>
            <label className="field">
              <span>File name</span>
              <input
                value={reqPath}
                onChange={(e) => setReqPath(e.target.value)}
                placeholder="report.md"
                autoComplete="off"
                disabled={requesting}
              />
            </label>
            <label className="field">
              <span>What should it contain?</span>
              <textarea
                className="message-box__input"
                value={instruct}
                onChange={(e) => setInstruct(e.target.value)}
                placeholder={`Write ${reqPath.trim() || "report.md"} covering…`}
                rows={3}
                disabled={requesting}
              />
            </label>
            <div className="message-box__actions">
              <button
                className="btn btn--queue"
                type="submit"
                disabled={requesting || !reqPath.trim()}
              >
                Queue
              </button>
              <button
                className="btn btn--interrupt"
                type="button"
                disabled={requesting || !reqPath.trim()}
                onClick={() => void onRequest("interrupt")}
              >
                Interrupt &amp; write now
              </button>
            </div>
          </>
        )}
      </form>
    </section>
  );
}
